// Tasks > Assistants — owner/director (all clinics) and manager (own clinic).
// Opened on demand, so opening the Tasks page itself makes no extra call (INV-1).
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { session } from '../lib/session';
import {
  listAssistants, createAssistant, newEnrolmentLink, disableAssistant, removeAssistantPasskey, resetAuthenticator,
  type AssistantRow,
} from '../lib/assistantApi';
import { QrCode } from './QrCode';
import { visibleClinics } from './ClinicTabs';

// Owner, 2026-11-02: Winter Garden only for now; must match ASSISTANT_PILOT_CLINICS in src/assistant.js.
export const ASSISTANT_PILOT_CLINICS = ['MEWG'];

export function canManageAssistants(role: string | null | undefined, clinics: string[] = []): boolean {
  if (role === 'OWNER' || role === 'DIRECTOR') return true;
  return role === 'MANAGER' && clinics.some((c) => ASSISTANT_PILOT_CLINICS.includes(c));
}

export function formatCountdown(msLeft: number): string {
  const s = Math.max(0, Math.ceil(msLeft / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

interface Link { assistantId: number; name: string; url: string; expiresAt: number }

function LinkCard({ link, onGone }: { link: Link; onGone: () => void }): JSX.Element {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const left = link.expiresAt - now;
  return (
    <div class="card" data-testid="assistant-link">
      <p>Have <strong>{link.name}</strong> scan this with their phone camera.</p>
      {left > 0 ? (
        <>
          <QrCode text={link.url} label={`Set-up code for ${link.name}`} />
          <p><code data-testid="assistant-link-url" style="word-break:break-all">{link.url}</code></p>
          <p data-testid="assistant-countdown">Expires in {formatCountdown(left)}</p>
        </>
      ) : (
        <p data-testid="assistant-link-expired">This code has expired. Use New link for a fresh one.</p>
      )}
      <button type="button" class="mini" onClick={onGone}>Close</button>
    </div>
  );
}

export function AssistantsPanel(): JSX.Element | null {
  const me = session.value.me;
  const role = me?.role;
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<AssistantRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const clinics = visibleClinics();
  const [clinic, setClinic] = useState<string>(clinics[0] ?? '');
  const [link, setLink] = useState<Link | null>(null);

  if (!canManageAssistants(role, clinics)) return null;

  async function reload(): Promise<void> {
    try {
      setRows((await listAssistants()).assistants);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function act(fn: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try { await fn(); } catch (err) { setError(err instanceof Error ? err.message : String(err)); } finally { setBusy(false); }
  }

  function toggle(): void {
    const next = !open;
    setOpen(next);
    if (next && rows === null) void reload();
  }

  const add = () => act(async () => {
    const clean = name.trim();
    if (!clean) { setError('Type the assistant\'s name first.'); return; }
    const r = await createAssistant(clean, role === 'MANAGER' ? undefined : clinic);
    setLink({ assistantId: r.assistant.id, name: r.assistant.display_name, url: r.enrolment_url, expiresAt: Date.parse(r.expires_at) });
    setName('');
    await reload();
  });

  const fresh = (a: AssistantRow) => act(async () => {
    const r = await newEnrolmentLink(a.id);
    setLink({ assistantId: a.id, name: a.display_name, url: r.enrolment_url, expiresAt: Date.parse(r.expires_at) });
    await reload();
  });

  const resetTotp = (a: AssistantRow) => act(async () => {
    const r = await resetAuthenticator(a.id);
    setLink({ assistantId: a.id, name: a.display_name, url: r.enrolment_url, expiresAt: Date.parse(r.expires_at) });
    await reload();
  });

  const disable = (a: AssistantRow) => act(async () => {
    await disableAssistant(a.id);
    if (link?.assistantId === a.id) setLink(null);
    await reload();
  });

  const removeKey = (a: AssistantRow, credentialId: string) => act(async () => {
    await removeAssistantPasskey(a.id, credentialId);
    await reload();
  });

  function statusOf(a: AssistantRow): string {
    if (!a.active) return 'Disabled';
    const parts = [a.passkeys.length ? 'phone' : null, a.totp_set ? 'authenticator' : null].filter(Boolean);
    return parts.length ? `Ready (${parts.join(', ')})` : 'Not set up';
  }
  function lastUsed(a: AssistantRow): string {
    const t = a.passkeys.map((k) => k.last_used_at).filter(Boolean).sort().pop();
    return t ? new Date(t).toLocaleString() : 'Never';
  }

  return (
    <div class={open ? 'card' : ''} data-testid="assistants-panel">
      <div class="section-head-row">
        {open ? <h2>Assistants</h2> : null}
        <button type="button" class="mini" data-testid="assistants-toggle" onClick={toggle}>
          {open ? 'Hide' : 'Assistants'}
        </button>
      </div>
      {open ? (
        <>
          <p>Assistants sign in with their phone (Face ID) or a Google Authenticator code, and can see Tasks for one clinic only.</p>
          <div>
            <input
              type="text" aria-label="Assistant name" placeholder="Name" maxLength={80}
              value={name} onInput={(e: Event) => setName((e.target as HTMLInputElement).value)}
            />
            {role !== 'MANAGER' && clinics.length > 1 ? (
              <select aria-label="Clinic" value={clinic} onChange={(e: Event) => setClinic((e.target as HTMLSelectElement).value)}>
                {clinics.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : null}
            <button type="button" data-testid="assistant-add" disabled={busy} onClick={() => void add()}>Add assistant</button>
          </div>
          {error ? <div role="alert" data-testid="assistants-error">{error}</div> : null}
          {link ? <LinkCard link={link} onGone={() => setLink(null)} /> : null}
          {rows === null ? <p>Loading...</p> : rows.length === 0 ? <p>No assistants yet.</p> : (
            <table data-testid="assistants-table">
              <thead><tr><th>Name</th><th>Username</th><th>Clinic</th><th>Status</th><th>Last used</th><th /></tr></thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} data-testid={`assistant-row-${a.id}`}>
                    <td>{a.display_name}</td>
                    <td data-testid={`assistant-username-${a.id}`}>{a.username ?? ''}</td>
                    <td>{a.location_code}</td>
                    <td>{statusOf(a)}</td>
                    <td>{lastUsed(a)}</td>
                    <td>
                      {a.active ? (
                        <>
                          <button type="button" class="mini" disabled={busy} data-testid={`assistant-newlink-${a.id}`} onClick={() => void fresh(a)}>New link</button>
                          <button type="button" class="mini" disabled={busy} data-testid={`assistant-resettotp-${a.id}`} onClick={() => void resetTotp(a)}>Reset authenticator</button>
                          <button type="button" class="mini" disabled={busy} data-testid={`assistant-disable-${a.id}`} onClick={() => void disable(a)}>Disable</button>
                        </>
                      ) : null}
                      {a.passkeys.map((k, i) => (
                        <button
                          key={k.credential_id} type="button" class="mini" disabled={busy}
                          data-testid={`assistant-removekey-${a.id}-${i}`}
                          onClick={() => void removeKey(a, k.credential_id)}
                        >
                          Remove phone {i + 1}
                        </button>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : null}
    </div>
  );
}
